// PortadaCursoSecciones.js — Secciones de la portada de un curso tipo INS
// (réplica de ins.ateneo.co, Divi). Todas se editan en Admin → Landing:
//
//   portada_acceso      banner con logos, foto, título y acceso por correo + código
//   texto_vermas        párrafos con «Ver más / Ver menos» y un video debajo
//   iconos_fondo        «El curso»: título píldora + iconos en círculo sobre foto
//   titulo_texto_imagen «Certificación»: título + texto a la izquierda, imagen a la derecha
//   modulos_grilla      «Contenido del curso»: módulos en 3 columnas + botón Ingresar
//   estaciones          «Taller presencial»: texto, recuadro y tarjetas con icono
//   videos_grilla       «Testimonios»: videos en columnas
//   registro_tarjeta    página de registro: fondo, logos, tarjeta con el formulario y franja con botones
//   banner_interno      banner de las páginas internas (logos + título + barra de colores)
//   menu_iconos         menú de íconos que se queda fijo arriba al hacer scroll
//   texto_videos        título píldora + texto a la izquierda y videos a la derecha
//   modulos_tarjetas    tarjetas de módulos con avance real, última actividad e «Ir al módulo»
//
// Los títulos «píldora» (TituloPildora) son los del original: pegados al borde
// izquierdo de la pantalla y redondeados a la derecha, con relleno o solo borde.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authService, mentor2Service, BACKEND_BASE } from '../../../services/api';
import { fmtFechaHoraBogota } from '../../../utils/fecha';
import { useAuth } from '../../../contexts/AuthContext';
import useEntregasPendientes, { esTutor } from '../../entregas/useEntregasPendientes';
import { resolverVideo } from './VideoSection';
import AuthModal from '../../AuthModal';

export const ACENTO = '#32b5a9';
const GRIS = '#575756';
const FUENTE = "'Nunito', Helvetica, Arial, Lucida, sans-serif";
// Evento con el que cualquier botón de la página lleva al formulario del banner.
export const EVENTO_IR_ACCESO = 'portada:ir-acceso';

export const urlImagen = (v) => (!v ? '' : (/^(https?:)?\/\//.test(v) || v.startsWith('/') || v.startsWith('data:') ? v : `${BACKEND_BASE}/${v}`));

// Texto con negritas/enlaces escritos por el admin (mismo criterio que el resto de secciones).
const Html = ({ html, as: Tag = 'div', ...rest }) => <Tag {...rest} dangerouslySetInnerHTML={{ __html: html || '' }} />;

const irDestino = (navigate, url) => {
  if (!url) return;
  if (/^https?:\/\//i.test(url)) window.location.href = url;
  else if (url.startsWith('#')) document.getElementById(url.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  else navigate(url);
};

// Ancho del contenido (1000px / 90% en el original) y cuánto sobra a cada lado.
// Sin ancho propio, cada sección usa el «Ancho del contenedor» de la landing (variable CSS).
const anchoContenido = (c, def = '1000px') => c.ancho_contenido || `var(--ancho-contenedor, ${def})`;
const anchoVar = (propio, def) => propio || `var(--ancho-contenedor, ${def})`;
const Contenido = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
`;

/* ---------- Título píldora ---------- */
const Pildora = styled.div`
  position: relative;
  display: ${p => (p.$centrado ? 'block' : 'inline-block')};
  box-sizing: border-box;
  font-family: ${FUENTE};
  ${p => (p.$centrado
    ? `width: ${p.$anchoCentrado}; max-width: 100%; margin: 0 auto 20px; border-radius: 30px; padding: 13px 30px 0; text-align: center;`
    : `border-radius: 0 30px 30px 0; padding: 13px 30px 0 0;
       /* La píldora arranca en el borde izquierdo de la pantalla, el texto queda alineado al contenido. */
       margin-left: calc((100vw - 100%) / -2); padding-left: calc((100vw - 100%) / 2);`)}
  ${p => (p.$estilo === 'borde'
    ? `border: 2px solid ${p.$borde}; ${p.$centrado ? '' : 'border-left: 0;'} background: transparent;`
    : `background: ${p.$fondo}; border: 2px solid ${p.$fondo}; ${p.$centrado ? '' : 'border-left: 0;'}`)}
  h2 {
    margin: 0; padding: 0 0 10px; line-height: 1em;
    font-family: ${FUENTE}; font-weight: ${p => p.$peso}; text-transform: uppercase;
    font-size: ${p => p.$tamano}; color: ${p => p.$color};
  }
  @media (max-width: 980px) { h2 { font-size: ${p => p.$tamanoMovil}; } }
`;
// El margen negativo necesita conocer el ancho del contenedor: se envuelve para que
// «100%» sea el ancho del contenido y no el de la píldora.
const PildoraFila = styled.div`
  width: 100%;
  ${p => (p.$centrado ? '' : 'overflow: visible;')}
`;

export const TituloPildora = ({ c, prefijo = 'titulo', def = {} }) => {
  const g = (k, d) => c[`${prefijo}_${k}`] ?? def[k] ?? d;
  const texto = c[prefijo];
  if (!texto) return null;
  const centrado = g('alineacion', 'izquierda') === 'centro';
  return (
    <PildoraFila $centrado={centrado}>
      <Pildora
        $centrado={centrado}
        $anchoCentrado={g('ancho', '450px')}
        $estilo={g('estilo', 'relleno')}
        $fondo={g('fondo', ACENTO)}
        $borde={g('borde', '#ffffff')}
        $color={g('color', '#ffffff')}
        $tamano={g('tamano', '38px')}
        $tamanoMovil={g('tamano_movil', '30px')}
        $peso={g('peso', 700)}
      >
        <h2>{texto}</h2>
      </Pildora>
    </PildoraFila>
  );
};

/* =====================================================================
 * 1. PORTADA CON ACCESO (correo + código)
 * ===================================================================== */
const Portada = styled.section`
  position: relative;
  overflow: hidden;
  font-family: ${FUENTE};
  background-color: ${p => p.$color};
  ${p => p.$img && `background-image: url(${p.$img});`}
  background-size: cover;
  background-position: ${p => p.$pos};
  padding-top: 10px;
  min-height: ${p => p.$alto};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;
const PortadaLogos = styled.div`
  width: 90%;
  margin: 0 auto;
  padding-top: ${p => p.$arriba};
  img { display: block; width: ${p => p.$ancho}; max-width: 100%; height: auto; }
  @media (max-width: 980px) { padding-top: 16px; img { margin: 0 auto; width: ${p => p.$anchoMovil}; } }
`;
const PortadaFila = styled.div`
  flex: 1;
  width: ${p => p.$anchoFila};
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${p => p.$columnas};
  align-items: end;
  .foto { align-self: end; line-height: 0; }
  .foto img { width: 100%; max-width: ${p => p.$anchoImagen}; height: auto; display: block; }
  .texto { align-self: center; color: #fff; padding: ${p => p.$textoPadding}; }
  /* Tamaño del título en escritorio (editable); en celular manda el de TituloPortada. */
  @media (min-width: 981px) {
    .texto h1 { font-size: ${p => p.$t2}; }
    .texto h1 .l1 { font-size: ${p => p.$t1}; }
    .texto h1 .l3 { font-size: ${p => p.$t3}; }
    /* «Iniciar sesión», «Verificar código» y «Ver módulos» (editable). */
    .texto button.boton-portada { font-size: ${p => p.$tBoton}; padding: 9px 19px; }
  }
  @media (max-width: 980px) {
    width: 90%;
    grid-template-columns: minmax(0, 1fr);
    .texto { order: -1; text-align: center; padding: 25px 0 10px; }
    .foto img { max-width: 480px; margin: 0 auto; }
  }
`;
const TituloPortada = styled.h1`
  margin: 0 0 18px; padding: 0;
  font-family: ${FUENTE}; color: #fff; text-transform: uppercase;
  font-size: 24px; font-weight: 300; line-height: 1.2;
  .l1 { display: block; font-weight: 800; font-size: 38px; }
  .l2 { display: block; }
  .l3 { display: inline-block; font-weight: 800; font-size: 49px; line-height: 1.2; }
  @media (min-width: 1400px) { font-size: 30px; .l1 { font-size: 52px; } .l3 { font-size: 68px; } }
  @media (max-width: 980px) { font-size: 19px; .l1 { font-size: 26px; } .l3 { font-size: 34px; } }
`;
const BotonBorde = styled.button`
  display: inline-block;
  border: 1px solid #fff; border-radius: 10px;
  background: transparent; color: #fff; cursor: pointer;
  padding: 9px 19px; margin: 0 9px 0 0;
  font-family: ${FUENTE}; font-weight: 700; font-size: 22px; line-height: 1; text-transform: uppercase;
  text-decoration: none;
  transition: transform .3s ease, background .3s ease;
  &:hover:not(:disabled) { transform: scale(1.05); background: rgba(255,255,255,.08); }
  &:disabled { opacity: .7; cursor: progress; }
  @media (min-width: 1400px) { font-size: 28px; padding: 12px 24px; }
`;
const FormAcceso = styled.form`
  display: flex; flex-direction: column; align-items: flex-start; gap: 14px; max-width: 420px;
  .campo { display: flex; align-items: center; gap: 8px; width: 100%; }
  .arroba { color: #fff; font-size: 30px; font-weight: 800; line-height: 1; }
  input {
    flex: 0 1 290px; min-width: 0; box-sizing: border-box;
    padding: 12px 14px; border: 2px solid ${ACENTO}; border-radius: 6px 7px;
    background: #fff; color: #333; font-family: ${FUENTE}; font-weight: 700; font-size: 20px;
    outline: none;
  }
  input:focus { box-shadow: 0 0 0 3px rgba(255,255,255,.55); }
  input.codigo { letter-spacing: 6px; text-align: center; font-size: 19px; padding: 9px 12px; max-width: 200px; }
  ${BotonBorde} { margin-left: ${p => (p.$conArroba ? '42px' : '0')}; }
  .aviso { color: #fff; font-size: 16px; line-height: 1.35; margin: 0; }
  .error {
    display: flex; gap: 12px; align-items: flex-start; box-sizing: border-box; width: 100%; max-width: 420px;
    margin: 0; padding: 12px 14px; border-radius: 10px; border-left: 4px solid ${p => p.$colorError};
    background: #fff; color: #3f3f3e; font-size: 15px; line-height: 1.4; text-align: left;
    box-shadow: 0 8px 24px -10px rgba(0, 0, 0, .35);
    animation: avisoEntra .25s ease-out;
  }
  .error .icono {
    flex: 0 0 26px; width: 26px; height: 26px; border-radius: 50%;
    background: ${p => p.$colorError}; color: #fff; font-weight: 900; font-size: 16px;
    display: flex; align-items: center; justify-content: center;
  }
  .error .titulo-aviso { display: block; font-weight: 800; color: ${p => p.$colorError}; margin-bottom: 2px; }
  .error a {
    display: inline-block; margin-top: 8px; color: ${ACENTO}; font-weight: 800; text-decoration: none;
    border-bottom: 2px solid ${ACENTO};
  }
  .error a:hover { color: #167e75; border-color: #167e75; }
  @keyframes avisoEntra { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
  .enlaces { display: flex; gap: 18px; flex-wrap: wrap; }
  .enlaces button { background: none; border: 0; padding: 0; color: #fff; text-decoration: underline; cursor: pointer; font: inherit; font-size: 15px; }
  @media (max-width: 980px) {
    align-items: center; margin: 0 auto;
    ${BotonBorde} { margin-left: 0; }
  }
`;
const Barra = styled.div`
  position: absolute; bottom: 0; height: 5px;
  left: ${p => p.$izq}; width: ${p => p.$ancho}; max-width: 45%;
  display: flex;
  span { flex: 1; }
  @media (max-width: 980px) { left: 5%; width: 90%; max-width: 90%; }
`;

export const PortadaAccesoSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [paso, setPaso] = useState('correo');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [noInscrito, setNoInscrito] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const refCorreo = useRef(null);
  const refSeccion = useRef(null);
  const destino = c.url_destino || '/contenidos';

  // «Ingresar» (u otro botón con la acción «acceso») trae aquí y deja el cursor en el correo.
  useEffect(() => {
    const ir = () => {
      refSeccion.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => refCorreo.current?.focus({ preventScroll: true }), 450);
    };
    window.addEventListener(EVENTO_IR_ACCESO, ir);
    return () => window.removeEventListener(EVENTO_IR_ACCESO, ir);
  }, []);

  const entrar = async (conCodigo) => {
    setError('');
    setNoInscrito(false);
    const correo = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      setError(tl(c.texto_error_correo, 'Escribe un correo válido.'));
      return;
    }
    if (conCodigo && codigo.replace(/\D/g, '').length !== 6) {
      setError(tl(c.texto_error_codigo, 'El código tiene 6 dígitos.'));
      return;
    }
    setEnviando(true);
    try {
      const res = await authService.loginPaso({ email: correo, passwordless: true, ...(conCodigo ? { codigo: codigo.replace(/\D/g, '') } : {}) });
      if (res.data?.requiere_codigo) {
        setPaso('codigo');
        setCodigo('');
        setAviso(res.data.message || '');
        return;
      }
      if (res.data?.token) {
        login({ user: res.data.user, token: res.data.token });
        setPaso('correo');
        if (c.ir_al_entrar !== false) irDestino(navigate, destino);
      }
    } catch (e) {
      const d = e.response?.data || {};
      setError(d.message || tl(c.texto_error_general, 'No pudimos iniciar sesión. Intenta de nuevo.'));
      setNoInscrito(d.code === 'no_inscrito' || d.code === 'sin_cuenta_proyecto');
    } finally {
      setEnviando(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    entrar(paso === 'codigo');
  };

  const filaAncho = c.ancho_fila || '92%';
  const colorBarra = Array.isArray(c.barra_colores) && c.barra_colores.length ? c.barra_colores : ['#f2e21c', '#243e7e', '#e1312a'];
  const botonesSesion = (Array.isArray(c.botones_sesion) && c.botones_sesion.length)
    ? c.botones_sesion
    : [{ texto: c.texto_boton_sesion || 'Ver módulos', url: destino }];

  return (
    <Portada
      ref={refSeccion}
      id={c.ancla || 'inicio'}
      $color={c.color_fondo || ACENTO}
      $img={urlImagen(c.imagen_fondo)}
      $pos={c.posicion_fondo || 'left bottom'}
      $alto={c.alto || 'min(57vw, 1097px)'}
    >
      {c.logos && (
        <PortadaLogos $arriba={c.logos_arriba || '60px'} $ancho={c.ancho_logos || '410px'} $anchoMovil={c.ancho_logos_movil || '300px'}>
          <img src={urlImagen(c.logos)} alt={c.logos_alt || 'Logos'} />
        </PortadaLogos>
      )}
      <PortadaFila $anchoFila={filaAncho} $columnas={c.columnas || '53% 47%'} $textoPadding={c.texto_padding || '4% 0 0'} $anchoImagen={c.ancho_imagen || '840px'}
        $t1={c.tamano_linea_1 || '40px'} $t2={c.tamano_linea_2 || '23px'} $t3={c.tamano_linea_3 || '52px'}
        $tBoton={c.tamano_boton || '19px'}>
        <div className="foto">
          {c.imagen && <img src={urlImagen(c.imagen)} alt={c.imagen_alt || ''} />}
        </div>
        <div className="texto">
          <TituloPortada>
            {c.titulo_1 && <span className="l1">{tl(c.titulo_1)}</span>}
            {c.titulo_2 && <span className="l2">{tl(c.titulo_2)}</span>}
            {c.titulo_3 && <span className="l3">{tl(c.titulo_3)}</span>}
          </TituloPortada>
          {user ? (
            <div>
              {botonesSesion.map((b, i) => (
                <BotonBorde key={i} type="button" className="boton-portada" onClick={() => irDestino(navigate, b.url || destino)}>{tl(b.texto, 'Ver módulos')}</BotonBorde>
              ))}
            </div>
          ) : (
            <FormAcceso onSubmit={onSubmit} $conArroba={c.mostrar_arroba !== false} $colorError={c.color_error || '#e1312a'} noValidate>
              {paso === 'correo' ? (
                <div className="campo">
                  {c.mostrar_arroba !== false && <span className="arroba" aria-hidden="true">@</span>}
                  <input
                    ref={refCorreo}
                    type="email"
                    autoComplete="email"
                    aria-label={tl(c.placeholder_correo, 'Ingrese email')}
                    placeholder={tl(c.placeholder_correo, 'Ingrese email')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              ) : (
                <>
                  <p className="aviso">{aviso || tl(c.texto_codigo_enviado, 'Te enviamos un código de 6 dígitos a tu correo.')}</p>
                  <div className="campo">
                    <input
                      className="codigo"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      autoFocus
                      aria-label={tl(c.placeholder_codigo, 'Código')}
                      placeholder={tl(c.placeholder_codigo, '••••••')}
                      value={codigo}
                      onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </div>
                </>
              )}
              {error && (
                <div className="error" role="alert">
                  <span className="icono" aria-hidden="true">!</span>
                  <div>
                    {noInscrito && <span className="titulo-aviso">{tl(c.titulo_no_inscrito, 'Correo no inscrito')}</span>}
                    {error}
                    {noInscrito && c.mostrar_registro_no_inscrito !== false && (
                      <div>
                        <a href={c.url_registro || '/register'} onClick={e => { e.preventDefault(); irDestino(navigate, c.url_registro || '/register'); }}>
                          {tl(c.texto_registro_no_inscrito, 'Regístrate aquí')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <BotonBorde type="submit" className="boton-portada" disabled={enviando}>
                {enviando
                  ? tl(c.texto_enviando, 'Un momento…')
                  : paso === 'codigo' ? tl(c.texto_boton_codigo, 'Verificar código') : tl(c.texto_boton_entrar, 'Iniciar sesión')}
              </BotonBorde>
              {paso === 'codigo' && (
                <div className="enlaces">
                  <button type="button" disabled={enviando} onClick={() => entrar(false)}>{tl(c.texto_reenviar, 'Reenviar código')}</button>
                  <button type="button" onClick={() => { setPaso('correo'); setError(''); setCodigo(''); }}>{tl(c.texto_cambiar_correo, 'Usar otro correo')}</button>
                </div>
              )}
            </FormAcceso>
          )}
        </div>
      </PortadaFila>
      {c.mostrar_barra === true && (
        <Barra $izq={c.barra_izquierda || '52.7%'} $ancho={c.barra_ancho || '37%'}>
          {colorBarra.map((col, i) => <span key={i} style={{ background: col }} />)}
        </Barra>
      )}
    </Portada>
  );
};

/* =====================================================================
 * 2. TEXTO CON «VER MÁS» + VIDEO
 * ===================================================================== */
const SeccionBlanca = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: ${FUENTE};
`;
const TextoGris = styled.div`
  color: ${p => p.$color};
  font-size: ${p => p.$tamano};
  line-height: 1.2em;
  text-align: ${p => p.$alinear};
  p { margin: 0 0 1em; padding: 0; }
  strong { color: ${p => p.$resaltado}; }
  @media (max-width: 980px) { font-size: ${p => p.$tamanoMovil}; }
`;
const BotonVerMas = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 6px;
  width: 30%; min-width: 200px; margin: 24px auto 0; padding: 10px;
  border: 2px solid ${p => p.$color}; border-radius: 9px; background: #fff;
  color: ${p => p.$color}; font-family: ${FUENTE}; font-weight: 700; font-size: 25px; cursor: pointer;
  box-shadow: 0 6px 18px -6px rgba(0,0,0,.3);
  svg { width: 23px; height: 23px; transition: transform .25s ease; }
  &.abierto svg { transform: rotate(180deg); }
`;
const MarcoVideo = styled.div`
  width: ${p => p.$ancho}; max-width: 100%; margin: ${p => p.$margen} auto 0; box-sizing: border-box;
  border: 2px solid ${p => p.$borde}; border-radius: 5px; padding: 10px; background: #fff;
  box-shadow: 0 2px 18px 0 rgba(0,0,0,.3);
  .ratio { position: relative; padding-top: 56.2963%; border-radius: 5px; overflow: hidden; background: #000; }
  iframe, video { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
  @media (max-width: 980px) { width: 100%; }
`;
const FlechaDoble = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 5 12 12 19 5" /><polyline points="5 12 12 19 19 12" />
  </svg>
);

export const VideoMarco = ({ url, titulo, ancho = '100%', margen = '0', borde = '#72c8c0' }) => {
  const v = resolverVideo(url);
  if (!v) return null;
  return (
    <MarcoVideo $ancho={ancho} $margen={margen} $borde={borde}>
      <div className="ratio">
        {v.tipo === 'archivo'
          ? <video src={v.src} controls preload="metadata" title={titulo || ''} />
          : <iframe src={v.src} title={titulo || 'Video'} loading="lazy" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />}
      </div>
    </MarcoVideo>
  );
};

export const TextoVerMasSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const [abierto, setAbierto] = useState(false);
  const color = c.color_acento || ACENTO;
  return (
    <SeccionBlanca $fondo={c.fondo || '#ffffff'} $padding={c.padding || '54px 0'}>
      <Contenido $ancho={anchoContenido(c)}>
        <TextoGris $color={c.color_texto || GRIS} $resaltado={color} $tamano={c.tamano_texto || '24px'} $tamanoMovil={c.tamano_texto_movil || '19px'} $alinear={c.alineacion || 'left'}>
          <Html html={tl(c.texto)} />
          {abierto && c.texto_oculto && <Html html={tl(c.texto_oculto)} />}
        </TextoGris>
        {c.texto_oculto && (
          <BotonVerMas type="button" className={abierto ? 'abierto' : ''} $color={color} aria-expanded={abierto} onClick={() => setAbierto(a => !a)}>
            <FlechaDoble />{abierto ? tl(c.texto_ver_menos, 'Ver menos') : tl(c.texto_ver_mas, 'Ver más')}
          </BotonVerMas>
        )}
        {c.video_url && <VideoMarco url={c.video_url} titulo={tl(c.video_titulo)} ancho={c.video_ancho || '83%'} margen="20px" borde={c.color_borde_video || '#72c8c0'} />}
      </Contenido>
    </SeccionBlanca>
  );
};

/* =====================================================================
 * 3. ICONOS SOBRE FOTO («El curso»)
 * ===================================================================== */
const SeccionFoto = styled.section`
  position: relative;
  font-family: ${FUENTE};
  background-color: ${p => p.$color};
  ${p => p.$img && `background-image: url(${p.$img});`}
  background-size: cover;
  background-position: center;
  padding: ${p => p.$padding};
`;
const GrillaIconos = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr));
  column-gap: 5.5%;
  margin-top: 40px;
  .item {
    display: flex; align-items: center; min-height: 194px; box-sizing: border-box;
    padding: 32px 0; color: ${p => p.$color}; font-size: 18px; line-height: 1.2em;
  }
  .item.linea { border-bottom: 1px solid ${p => p.$linea}; }
  .circulo {
    flex: 0 0 90px; width: 90px; height: 90px; border-radius: 50%; background: ${p => p.$circulo};
    display: flex; align-items: center; justify-content: center; margin-right: 16px;
  }
  .circulo img { height: 55px; width: auto; max-width: 70px; transition: transform .3s ease; }
  .item:hover .circulo img { transform: scale(1.1); }
  .item strong { display: block; text-transform: uppercase; font-weight: 700; }
  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
    .item { min-height: 0; }
    .item.linea, .item:not(:last-child) { border-bottom: 1px solid ${p => p.$linea}; }
  }
`;

export const IconosFondoSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const items = Array.isArray(c.items) ? c.items : [];
  const cols = Number(c.columnas) || 3;
  // Línea bajo cada ítem salvo los de la última fila (como el original).
  const corte = Math.floor((items.length - 1) / cols) * cols;
  return (
    <SeccionFoto $color={c.color_fondo || ACENTO} $img={urlImagen(c.imagen_fondo)} $padding={c.padding || '65px 0 73px'}>
      <Contenido $ancho={anchoContenido(c)}>
        <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ estilo: 'borde', borde: '#ffffff', tamano: '48px' }} />
        <GrillaIconos $cols={cols} $color={c.color_texto || '#ffffff'} $linea={c.color_linea || '#ffffff'} $circulo={c.color_circulo || '#ffffff'}>
          {items.map((it, i) => (
            <div key={it.id || i} className={`item${i < corte ? ' linea' : ''}`}>
              {it.icono && <span className="circulo"><img src={urlImagen(it.icono)} alt="" /></span>}
              <div>
                {it.titulo && <strong>{tl(it.titulo)}</strong>}
                {it.texto && <Html as="span" html={tl(it.texto)} />}
              </div>
            </div>
          ))}
        </GrillaIconos>
      </Contenido>
    </SeccionFoto>
  );
};

/* =====================================================================
 * 4. TÍTULO + TEXTO + IMAGEN («Certificación»)
 * ===================================================================== */
const FilaDos = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 5.5%;
  align-items: center;
  ${p => p.$invertir && '.col-imagen { order: -1; }'}
  .col-imagen { text-align: center; }
  .col-imagen img { max-width: 100%; height: auto; }
  @media (max-width: 980px) { grid-template-columns: minmax(0, 1fr); gap: 24px; }
`;

export const TituloTextoImagenSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  return (
    <SeccionBlanca $fondo={c.fondo || '#ffffff'} $padding={c.padding || '54px 0'}>
      <Contenido $ancho={anchoContenido(c)}>
        <FilaDos $invertir={c.imagen_izquierda === true}>
          <div>
            <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ alineacion: 'centro', tamano: '38px' }} />
            <TextoGris $color={c.color_texto || GRIS} $resaltado={c.color_acento || ACENTO} $tamano={c.tamano_texto || '25px'} $tamanoMovil={c.tamano_texto_movil || '20px'} $alinear={c.alineacion || 'center'} style={{ padding: '0 8%' }}>
              <Html html={tl(c.texto)} />
            </TextoGris>
          </div>
          <div className="col-imagen">{c.imagen && <img src={urlImagen(c.imagen)} alt={c.imagen_alt || ''} style={{ width: c.ancho_imagen || '344px' }} />}</div>
        </FilaDos>
      </Contenido>
    </SeccionBlanca>
  );
};

/* =====================================================================
 * 5. MÓDULOS EN GRILLA + BOTÓN INGRESAR
 * ===================================================================== */
const GrillaModulos = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr));
  padding-top: 40px;
  .modulo {
    padding: 30px; border-bottom: 1px solid ${p => p.$color}; color: ${p => p.$texto};
    font-size: 19px; line-height: 1.2em; transition: transform .3s ease;
  }
  .modulo:hover { transform: scale(1.03); }
  .modulo h3 { margin: 0 0 6px; padding: 0; font-family: ${FUENTE}; font-weight: 800; text-transform: uppercase; font-size: 28px; color: ${p => p.$color}; }
  .modulo p { margin: 0; }
  .modulo.boton { display: flex; align-items: flex-start; }
  @media (max-width: 980px) { grid-template-columns: minmax(0, 1fr); }
`;
const BotonIngresar = styled.button`
  position: relative; width: 100%; box-sizing: border-box;
  padding: 22px 31px 19px; border: 0; border-radius: 22px; cursor: pointer;
  background: ${p => p.$color}; color: #fff;
  font-family: ${FUENTE}; font-weight: 800; font-size: 34px; line-height: 1; text-transform: uppercase;
  transition: background .3s ease;
  &::before {
    content: ''; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    width: 90%; height: 71%; border: 1px solid #fff; border-radius: 9px; pointer-events: none;
  }
  &:hover { background: ${p => p.$hover}; }
  @media (max-width: 980px) { font-size: 25px; }
`;

// Acción de un botón: «acceso» → sin sesión lleva al formulario del banner; con sesión, a la URL.
export const useAccionBoton = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (accion, url) => {
    if (accion === 'acceso' && !user) {
      window.dispatchEvent(new Event(EVENTO_IR_ACCESO));
      return;
    }
    irDestino(navigate, url || '/contenidos');
  };
};

export const ModulosGrillaSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const items = Array.isArray(c.items) ? c.items : [];
  const accionar = useAccionBoton();
  const color = c.color_acento || ACENTO;
  return (
    <SeccionBlanca $fondo={c.fondo || 'transparent'} $padding={c.padding || '20px 0 83px'}>
      <Contenido $ancho={anchoContenido(c)}>
        <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ fondo: color, borde: color, tamano: '39px', peso: 800 }} />
        <GrillaModulos $cols={Number(c.columnas) || 3} $color={color} $texto={c.color_texto || GRIS}>
          {items.map((m, i) => (
            <div key={m.id || i} className="modulo">
              {m.titulo && <h3>{tl(m.titulo)}</h3>}
              {m.texto && <Html as="p" html={tl(m.texto)} />}
            </div>
          ))}
          {c.boton_texto && (
            <div className="modulo boton">
              <BotonIngresar type="button" $color={color} $hover={c.color_hover || '#167e75'} onClick={() => accionar(c.boton_accion || 'acceso', c.boton_url)}>
                {tl(c.boton_texto)}
              </BotonIngresar>
            </div>
          )}
        </GrillaModulos>
      </Contenido>
    </SeccionBlanca>
  );
};

/* =====================================================================
 * 6. ESTACIONES («Taller presencial de simulación»)
 * ===================================================================== */
const Recuadro = styled.div`
  margin: 20px 0 0; padding: 10px 10px 7px; border: 1px solid ${p => p.$borde}; border-radius: 50px;
  color: #fff; text-align: center; text-transform: uppercase; font-size: 24px; line-height: 1.2em;
  p { margin: 0; }
  @media (max-width: 980px) { font-size: 18px; border-radius: 24px; }
`;
const GrillaEstaciones = styled.div`
  width: 90%; max-width: ${p => p.$ancho}; margin: 30px auto 0;
  display: grid; grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr)); gap: 3%;
  .estacion { display: flex; flex-direction: column; align-items: center; }
  .circulo {
    position: relative; z-index: 2; width: 90px; height: 90px; border-radius: 50%;
    background: ${p => p.$color}; border: 1px solid #fff; box-sizing: border-box;
    display: flex; align-items: center; justify-content: center; margin-bottom: -69px;
  }
  .circulo img { height: 55px; width: auto; max-width: 70px; transition: transform .3s ease; }
  .estacion:hover .circulo img { transform: scale(1.1); }
  .tarjeta {
    width: 100%; box-sizing: border-box; flex: 1; margin-top: 20px;
    background: #fff; border-radius: 16px; padding: 70px 22px 30px; text-align: center;
    color: ${GRIS}; font-size: 19px; line-height: 1.2em; transition: transform .3s ease;
  }
  .tarjeta:hover { transform: scale(1.03); }
  .tarjeta h3 { margin: 0 0 8px; padding: 0; font-family: ${FUENTE}; font-weight: 700; font-size: 28px; color: ${p => p.$color}; }
  .tarjeta p { margin: 0; }
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 24px; }
  @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); }
`;

export const EstacionesSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const items = Array.isArray(c.items) ? c.items : [];
  const color = c.color_acento || ACENTO;
  return (
    <SeccionFoto $color={c.color_fondo || ACENTO} $img={urlImagen(c.imagen_fondo)} $padding={c.padding || '65px 0 63px'}>
      <Contenido $ancho={anchoContenido(c)}>
        <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ estilo: 'borde', borde: '#ffffff', tamano: '38px' }} />
        {c.texto && (
          <TextoGris $color="#ffffff" $resaltado="#ffffff" $tamano={c.tamano_texto || '27px'} $tamanoMovil="20px" $alinear="left" style={{ marginTop: 27 }}>
            <Html html={tl(c.texto)} />
          </TextoGris>
        )}
        {c.recuadro && <Recuadro $borde={c.color_borde_recuadro || '#ffffff'}><Html html={tl(c.recuadro)} /></Recuadro>}
      </Contenido>
      <GrillaEstaciones $cols={Number(c.columnas) || items.length || 5} $color={color} $ancho={anchoVar(c.ancho_tarjetas, '1200px')}>
        {items.map((it, i) => (
          <div key={it.id || i} className="estacion">
            {it.icono && <span className="circulo"><img src={urlImagen(it.icono)} alt="" /></span>}
            <div className="tarjeta">
              {it.titulo && <h3>{tl(it.titulo)}</h3>}
              {it.texto && <Html as="p" html={tl(it.texto)} />}
            </div>
          </div>
        ))}
      </GrillaEstaciones>
    </SeccionFoto>
  );
};

/* =====================================================================
 * 7. VIDEOS EN GRILLA («Testimonios»)
 * ===================================================================== */
const GrillaVideos = styled.div`
  width: 90%; max-width: ${p => p.$ancho}; margin: 30px auto 0;
  display: grid; grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr)); gap: 5.5%;
  .titulo-video { margin: 10px 0 0; text-align: center; color: ${GRIS}; font-size: 17px; }
  @media (max-width: 980px) { grid-template-columns: minmax(0, 1fr); row-gap: 30px; }
`;

export const VideosGrillaSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const videos = (Array.isArray(c.videos) ? c.videos : []).filter(v => v.url);
  return (
    <SeccionBlanca $fondo={c.fondo || '#e8f7f6'} $padding={c.padding || '54px 0'}>
      <Contenido $ancho={anchoContenido(c)}>
        <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ estilo: 'borde', borde: ACENTO, color: ACENTO, tamano: '30px', tamano_movil: '20px' }} />
      </Contenido>
      <GrillaVideos $cols={Number(c.columnas) || 3} $ancho={anchoVar(c.ancho_videos, '1728px')}>
        {videos.map((v, i) => (
          <div key={v.id || i}>
            <VideoMarco url={v.url} titulo={tl(v.titulo)} borde={c.color_borde_video || '#72c8c0'} />
            {v.mostrar_titulo && v.titulo && <p className="titulo-video">{tl(v.titulo)}</p>}
          </div>
        ))}
      </GrillaVideos>
    </SeccionBlanca>
  );
};

/* =====================================================================
 * Pie con curva («Recuerde» + logos) — footer `modo: 'curva'`
 * ===================================================================== */
const Pie = styled.footer`
  position: relative;
  font-family: ${FUENTE};
  background: ${p => p.$fondo};
  ${p => p.$curva && `background-image: url(${p.$curva}); background-repeat: no-repeat; background-position: left bottom; background-size: 100% auto;`}
  padding: ${p => p.$padding};
  min-height: ${p => p.$alto};
  box-sizing: border-box;
  .aviso { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 3fr); align-items: center; }
  .aviso .img { text-align: center; border-right: 1px solid ${p => p.$color}; padding: 10px 0; }
  .aviso .img img { max-width: 100%; height: auto; }
  .aviso .txt { padding-left: 5.5%; color: ${p => p.$color}; text-transform: uppercase; font-size: 28px; line-height: 1.3em; }
  .aviso .txt p { margin: 0; }
  .logos { display: flex; justify-content: flex-end; padding: 90px 50px 0; }
  .logos img { width: ${p => p.$anchoLogos}; max-width: 100%; height: auto; }
  .logo-izq { position: absolute; left: 28px; bottom: 21px; width: ${p => p.$anchoIzq}; }
  .logo-izq img { width: 100%; height: auto; display: block; }
  .copy { position: absolute; right: 50px; bottom: 21px; color: #fff; font-size: 13px; }
  @media (max-width: 980px) {
    .aviso { grid-template-columns: minmax(0, 1fr); text-align: center; }
    .aviso .img { border-right: 0; }
    .aviso .txt { padding: 14px 0 0; font-size: 20px; }
    .logos { justify-content: center; padding: 30px 20px 0; }
    .logo-izq { left: 10px; bottom: 17px; width: 83px; }
  }
`;

export const PieCurva = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const color = c.color_acento || ACENTO;
  return (
    <Pie
      $fondo={c.fondo || '#ffffff'}
      $curva={urlImagen(c.imagen_curva)}
      $padding={c.padding || '80px 0 74px'}
      $alto={c.alto || 'calc(100vw * 500 / 1920)'}
      $color={color}
      $anchoLogos={c.ancho_logos || '350px'}
      $anchoIzq={c.ancho_logo_izq || '171px'}
    >
      {(c.aviso_imagen || c.aviso_texto) && (
        <Contenido $ancho={anchoContenido(c, '1200px')}>
          <div className="aviso">
            <div className="img">{c.aviso_imagen && <img src={urlImagen(c.aviso_imagen)} alt={c.aviso_alt || ''} />}</div>
            <div className="txt"><Html html={tl(c.aviso_texto)} /></div>
          </div>
        </Contenido>
      )}
      {c.logos && <div className="logos"><img src={urlImagen(c.logos)} alt={c.logos_alt || 'Logos'} /></div>}
      {c.logo_izq && (
        <a className="logo-izq" href={c.logo_izq_url || undefined} target={c.logo_izq_url ? '_blank' : undefined} rel="noreferrer">
          <img src={urlImagen(c.logo_izq)} alt={c.logo_izq_alt || ''} />
        </a>
      )}
      {c.texto_copyright && <div className="copy">{tl(c.texto_copyright)}</div>}
    </Pie>
  );
};

/* =====================================================================
 * 8. REGISTRO EN TARJETA (página /register de INS)
 * El formulario es el AuthModal embebido: los campos salen del registro del
 * proyecto (ficha → Login y registro). Esta sección pone fondo, logos,
 * tarjeta, título y la franja de abajo con sus botones.
 * ===================================================================== */
const PaginaRegistro = styled.section`
  min-height: 100vh;
  box-sizing: border-box;
  padding: ${p => p.$padding};
  font-family: ${FUENTE};
  background-color: ${p => p.$color};
  ${p => p.$img && `background-image: url(${p.$img}); background-size: cover; background-position: center; background-attachment: fixed;`}
  .logos { display: flex; justify-content: center; align-items: center; gap: ${p => p.$gapLogos}; margin-bottom: 30px; flex-wrap: wrap; }
  .logos a { display: inline-block; line-height: 0; transition: transform .25s ease; }
  .logos a:hover { transform: scale(1.04); }
  .logos img { height: auto; max-width: 45vw; }
  @media (max-width: 980px) { background-attachment: scroll; }
`;
const TarjetaRegistro = styled.div`
  width: 90%; max-width: ${p => p.$ancho}; margin: 0 auto; box-sizing: border-box;
  border-radius: ${p => p.$radio}; overflow: hidden;
  .cuerpo { background: ${p => p.$fondo}; padding: 30px 44px 36px; }
  h1 { margin: 0 0 22px; text-align: center; font-family: ${FUENTE}; font-weight: 800; font-size: 40px; color: ${p => p.$colorTitulo}; }
  .pie { background: ${p => p.$fondoPie}; color: #fff; text-align: center; padding: 22px 20px 28px; font-size: 22px; }
  .pie p { margin: 0 0 16px; }
  .pie .botones { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
  .pie button {
    border: 1px solid #fff; border-radius: 10px; background: transparent; color: #fff; cursor: pointer;
    padding: 9px 22px; font-family: ${FUENTE}; font-weight: 700; font-size: 22px; line-height: 1; text-transform: uppercase;
    transition: transform .3s ease;
  }
  .pie button:hover { transform: scale(1.05); }
  @media (max-width: 600px) {
    .cuerpo { padding: 24px 18px 28px; }
    h1 { font-size: 30px; }
    .pie, .pie button { font-size: 18px; }
  }
`;

export const RegistroTarjetaSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const logos = (Array.isArray(c.logos) ? c.logos : []).filter(l => l.imagen);
  const botones = Array.isArray(c.botones) ? c.botones : [];
  // Estilo del formulario para esta tarjeta; el resto de la config del modal del proyecto se respeta.
  const override = {
    ocultar_encabezado: true,
    ocultar_tabs: true,
    ocultar_switch_link: true,
    etiquetas_en_placeholder: c.mostrar_etiquetas !== true,
    ocultar_asterisco: c.mostrar_asterisco !== true,
    ancho_modal: '100%',
    color_fondo_modal: 'transparent',
    sombra_modal: 'none',
    borde_modal: 'none',
    padding_formulario: '0',
    color_fondo_input: c.color_fondo_campos || '#ffffff',
    color_borde_input: c.color_borde_campos || '#e4e4e4',
    color_borde_input_focus: c.color_boton || '#1d5f5a',
    radio_inputs: c.radio_campos || '3px',
    color_texto: c.color_texto || '#555555',
    color_primario: c.color_boton || '#1d5f5a',
    color_secundario: c.color_boton || '#1d5f5a',
    texto_boton_registro: tl(c.texto_boton, 'Registro'),
    alineacion_boton: 'center',
    ancho_boton: c.ancho_boton || 'auto',
    padding_boton: c.padding_boton || '12px 26px',
    radio_botones: c.radio_boton || '8px',
    fuente: FUENTE,
    ancho_minimo_campo: c.ancho_minimo_campo || '200px',
  };
  if (c.url_tras_registro) override.url_destino_registro = c.url_tras_registro;
  return (
    <PaginaRegistro
      $color={c.color_fondo || ACENTO}
      $img={urlImagen(c.imagen_fondo)}
      $padding={c.padding || '30px 0 60px'}
      $gapLogos={c.separacion_logos || '6px'}
    >
      {logos.length > 0 && (
        <div className="logos">
          {logos.map((l, i) => {
            // Cada logo lleva a su enlace; sin enlace propio, al de la sección (la portada por defecto).
            const url = l.url || c.url_logos || '/';
            return (
              <a key={l.id || i} href={url} title={l.alt || 'Inicio'}
                onClick={e => { if (!/^https?:\/\//i.test(url)) { e.preventDefault(); irDestino(navigate, url); } }}>
                <img src={urlImagen(l.imagen)} alt={l.alt || ''} style={{ width: l.ancho || 'auto' }} />
              </a>
            );
          })}
        </div>
      )}
      <TarjetaRegistro
        $ancho={c.ancho_tarjeta || '780px'}
        $radio={c.radio_tarjeta || '8px'}
        $fondo={c.fondo_tarjeta || 'rgba(255,255,255,0.68)'}
        $fondoPie={c.fondo_pie || '#1d5f5a'}
        $colorTitulo={c.color_titulo || '#1d5f5a'}
      >
        <div className="cuerpo">
          {c.titulo && <h1>{tl(c.titulo)}</h1>}
          <AuthModalEmbebido override={override} onClose={() => irDestino(navigate, c.url_tras_registro || '/')} />
        </div>
        {(c.pie_texto || botones.length > 0) && (
          <div className="pie">
            {c.pie_texto && <p>{tl(c.pie_texto)}</p>}
            <div className="botones">
              {botones.map((b, i) => (
                <button key={b.id || i} type="button" onClick={() => irDestino(navigate, b.url || '/')}>{tl(b.texto)}</button>
              ))}
            </div>
          </div>
        )}
      </TarjetaRegistro>
    </PaginaRegistro>
  );
};

const AuthModalEmbebido = ({ override, onClose }) => (
  <AuthModal isOpen embedded embedPadding="0" initialTab="register" showLoginTab={false} onClose={onClose} configOverride={override} />
);

/* =====================================================================
 * 9. BANNER INTERNO (/modulo y demás páginas del curso)
 * ===================================================================== */
const BannerInterno = styled.section`
  position: relative; box-sizing: border-box; font-family: ${FUENTE};
  min-height: ${p => p.$alto};
  background-color: ${p => p.$color};
  ${p => p.$img && `background-image: url(${p.$img}); background-size: cover; background-position: ${p.$pos};`}
  display: flex; align-items: stretch;
  .fila { width: 90%; max-width: var(--ancho-contenedor, 1640px); margin: 0 auto; display: flex; justify-content: space-between; gap: 40px;
    padding: ${p => p.$padding}; box-sizing: border-box; }
  /* Como en ins.ateneo.co: los logos arriba a la izquierda y el título abajo a la derecha. */
  .logos { align-self: ${p => p.$alinLogos}; }
  .logos img { display: block; width: ${p => p.$anchoLogos}; max-width: 100%; height: auto; }
  .texto { flex: 0 1 ${p => p.$anchoTexto}; color: #fff; align-self: ${p => p.$alinTexto}; }
  .texto h1 { font-size: ${p => p.$t2}; }
  .texto h1 .l1 { font-size: ${p => p.$t1}; }
  .texto h1 .l3 { font-size: ${p => p.$t3}; }
  .barra { display: flex; height: 7px; margin-top: 14px; }
  .barra span { flex: 1; }
  @media (max-width: 980px) {
    .fila { flex-direction: column; text-align: center; gap: 16px; padding: 24px 0; }
    .logos, .texto { align-self: auto; }
    .logos img { margin: 0 auto; width: ${p => p.$anchoLogosMovil}; }
    .texto { flex: none; width: 100%; }
    .texto h1 { font-size: 18px; } .texto h1 .l1 { font-size: 26px; } .texto h1 .l3 { font-size: 34px; }
  }
`;

export const BannerInternoSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const colores = Array.isArray(c.barra_colores) && c.barra_colores.length ? c.barra_colores : ['#f2e21c', '#243e7e', '#e1312a'];
  return (
    <BannerInterno
      $alto={c.alto || '394px'} $color={c.color_fondo || ACENTO} $img={urlImagen(c.imagen_fondo)} $pos={c.posicion_fondo || 'center'}
      $anchoLogos={c.ancho_logos || '410px'} $anchoLogosMovil={c.ancho_logos_movil || '280px'} $anchoTexto={c.ancho_texto || '740px'}
      $padding={c.padding || '70px 0 44px'}
      $alinLogos={{ arriba: 'flex-start', centro: 'center', abajo: 'flex-end' }[c.posicion_logos || 'arriba']}
      $alinTexto={{ arriba: 'flex-start', centro: 'center', abajo: 'flex-end' }[c.posicion_titulo || 'abajo']}
      $t1={c.tamano_linea_1 || '46px'} $t2={c.tamano_linea_2 || '23px'} $t3={c.tamano_linea_3 || '60px'}
    >
      <div className="fila">
        <div className="logos">
          {c.logos && (
            <a href={c.url_logos || '/'} onClick={e => { e.preventDefault(); irDestino(navigate, c.url_logos || '/'); }}>
              <img src={urlImagen(c.logos)} alt={c.logos_alt || 'Logos'} />
            </a>
          )}
        </div>
        <div className="texto">
          <TituloPortada style={{ margin: 0 }}>
            {c.titulo_1 && <span className="l1">{tl(c.titulo_1)}</span>}
            {c.titulo_2 && <span className="l2">{tl(c.titulo_2)}</span>}
            {c.titulo_3 && <span className="l3">{tl(c.titulo_3)}</span>}
          </TituloPortada>
          {c.mostrar_barra !== false && <div className="barra">{colores.map((col, i) => <span key={i} style={{ background: col }} />)}</div>}
        </div>
      </div>
    </BannerInterno>
  );
};

/* =====================================================================
 * 10. MENÚ DE ÍCONOS (se queda fijo arriba al llegar a él con el scroll)
 * ===================================================================== */
const MenuIconosCaja = styled.nav`
  /* Al llegar arriba pasa a «fixed» y se achica; el hueco lo guarda el contenedor
     (MenuIconosHueco), así la página no se mueve y no hay saltos en el borde. */
  ${p => (p.$pegado ? 'position: fixed; top: 0; left: 0; right: 0;' : 'position: relative;')}
  z-index: 40;
  background: ${p => p.$fondo};
  font-family: ${FUENTE};
  transition: box-shadow .25s ease, padding .25s ease;
  padding: ${p => (p.$compacto ? '8px 0' : p.$padding)};
  box-shadow: ${p => (p.$pegado ? '0 6px 18px -8px rgba(0,0,0,.25)' : 'none')};
  ul { list-style: none; margin: 0 auto; padding: 0; width: 90%; max-width: var(--ancho-contenedor, 1400px); display: grid; grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr)); gap: 10px; }
  a { display: flex; flex-direction: column; align-items: center; text-decoration: none; color: ${p => p.$color}; text-align: center; }
  .icono { position: relative; width: ${p => (p.$compacto ? '48px' : p.$tamano)}; transition: width .25s ease; }
  .icono img { width: 100%; height: auto; display: block; transition: transform .3s ease; }
  a:hover .icono img { transform: scale(1.08); }
  .activo .icono::after {
    content: ''; position: absolute; left: 50%; bottom: -9px; transform: translateX(-50%);
    border: 9px solid transparent; border-bottom: 0; border-top-color: ${p => p.$color};
  }
  .texto { margin-top: ${p => (p.$compacto ? '6px' : '14px')}; text-transform: uppercase; line-height: 1.1;
    font-size: ${p => (p.$compacto ? '11px' : p.$tamanoTexto)}; transition: font-size .25s ease; }
  @media (max-width: 767px) {
    ul { grid-template-columns: repeat(${p => Math.min(p.$cols, 3)}, minmax(0, 1fr)); row-gap: 14px; }
    .icono { width: ${p => (p.$compacto ? '38px' : '60px')}; }
    .texto { font-size: 11px; }
  }
`;

// Ícono por defecto del ítem «Entregas por revisar» (bandeja con un documento), en el color del menú.
const iconoEntregas = (color) => 'data:image/svg+xml;utf8,' + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="${color}" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">`
  + '<path d="M8 38l7-22a4 4 0 0 1 3.8-2.8h26.4A4 4 0 0 1 49 16l7 22v12a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4z"/>'
  + '<path d="M8 38h14l3 6h14l3-6h14"/><path d="M26 22h12M26 29h12"/></svg>');

export const MenuIconosSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const { user } = useAuth();
  // Tutor de entregas: al final, el acceso a su bandeja con las pendientes.
  const verEntregas = esTutor(user) && !c.entregas_ocultar;
  const { pendientes } = useEntregasPendientes(verEntregas ? user : null);
  const items = [
    ...(Array.isArray(c.items) ? c.items : []).filter(it => it.icono || it.texto),
    ...(verEntregas ? [{ id: '__entregas', url: '/entregas', icono: c.entregas_icono || iconoEntregas(c.color || ACENTO),
      texto: `${tl(c.entregas_texto, 'Entregas por revisar')}${pendientes ? ` (${pendientes})` : ''}` }] : []),
  ];
  const ruta = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') || '/' : '/';
  const fijo = c.fijo !== false;
  const refHueco = useRef(null);
  const refMenu = useRef(null);
  const [pegado, setPegado] = useState(false);
  const [alto, setAlto] = useState(null);
  // Alto del menú sin achicar: el hueco lo conserva mientras el menú va fijo arriba.
  // Se vuelve a medir cada vez que el menú cambia de tamaño (p. ej. al cargar los íconos).
  useEffect(() => {
    const nav = refMenu.current;
    if (!nav || pegado) return undefined;
    const medir = () => { if (refMenu.current) setAlto(refMenu.current.offsetHeight); };
    medir();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', medir);
      return () => window.removeEventListener('resize', medir);
    }
    const ro = new ResizeObserver(medir);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [pegado, items.length]);
  // Se pega cuando el borde de arriba del hueco pasa el borde de la pantalla. Como el
  // hueco nunca cambia de alto, entrar y salir no mueve la página (sin rebotes).
  useEffect(() => {
    if (!fijo) return undefined;
    const revisar = () => {
      if (!refHueco.current) return;
      setPegado(refHueco.current.getBoundingClientRect().top < 0);
    };
    revisar();
    window.addEventListener('scroll', revisar, { passive: true });
    window.addEventListener('resize', revisar);
    return () => { window.removeEventListener('scroll', revisar); window.removeEventListener('resize', revisar); };
  }, [fijo]);
  if (!items.length) return null;
  return (
    // Mientras está en su sitio el hueco crece con el menú; solo al pegarse arriba guarda su alto.
    <div ref={refHueco} style={fijo && pegado && alto ? { height: alto } : undefined}>
      <MenuIconosCaja
        ref={refMenu}
        $pegado={fijo && pegado} $compacto={fijo && pegado && c.compacto_al_fijar !== false}
        $fondo={c.fondo || '#ffffff'} $color={c.color || ACENTO}
        $cols={items.length} $tamano={c.tamano_icono || '112px'} $tamanoTexto={c.tamano_texto || '20px'} $padding={c.padding || '52px 0 44px'}
        aria-label={tl(c.etiqueta_menu, 'Menú del curso')}
      >
        <ul>
          {items.map((it, i) => {
            const url = it.url || '/';
            const activo = url.replace(/\/+$/, '') === ruta;
            return (
              <li key={it.id || i} className={activo ? 'activo' : ''}>
                <a href={url} aria-current={activo ? 'page' : undefined}
                  onClick={e => { if (!/^https?:\/\//i.test(url)) { e.preventDefault(); irDestino(navigate, url); } }}>
                  {it.icono && <span className="icono"><img src={urlImagen(it.icono)} alt="" /></span>}
                  <span className="texto">{tl(it.texto)}</span>
                </a>
              </li>
            );
          })}
        </ul>
      </MenuIconosCaja>
    </div>
  );
};

/* =====================================================================
 * 11. TEXTO + VIDEOS (bienvenida de /modulo)
 * ===================================================================== */
const FilaTextoVideos = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 5.5%; margin-top: 40px; align-items: start;
  .videos { display: flex; flex-direction: column; gap: 28px; }
  a { color: ${p => p.$acento}; font-weight: 700; text-decoration: none; }
  a:hover { text-decoration: underline; }
  @media (max-width: 980px) { grid-template-columns: minmax(0, 1fr); gap: 28px; }
`;

export const TextoVideosSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const videos = (Array.isArray(c.videos) ? c.videos : []).filter(v => v.url);
  const acento = c.color_acento || ACENTO;
  return (
    <SeccionBlanca $fondo={c.fondo || '#e8f7f6'} $padding={c.padding || '100px 0 120px'}>
      <Contenido $ancho={anchoContenido(c, '1500px')}>
        <TituloPildora c={{ ...c, titulo: tl(c.titulo) }} def={{ estilo: 'borde', borde: acento, color: acento, tamano: '22px', tamano_movil: '17px', peso: 800 }} />
        <FilaTextoVideos $acento={acento}>
          <TextoGris $color={c.color_texto || GRIS} $resaltado={acento} $tamano={c.tamano_texto || '1em'} $tamanoMovil={c.tamano_texto_movil || '1em'} $alinear="left">
            <Html html={tl(c.texto)} />
          </TextoGris>
          <div className="videos">
            {videos.map((v, i) => <VideoMarco key={v.id || i} url={v.url} titulo={tl(v.titulo)} borde={c.color_borde_video || '#72c8c0'} />)}
          </div>
        </FilaTextoVideos>
      </Contenido>
    </SeccionBlanca>
  );
};

/* =====================================================================
 * 12. TARJETAS DE MÓDULOS (/modulo): ícono, título, avance y «Ir al módulo»
 * Cada tarjeta puede ir enlazada a un módulo real del modo Mentor
 * (documento_id + modulo_id): de ahí salen el % y la última actividad.
 * ===================================================================== */
const GrillaTarjetas = styled.div`
  width: 90%; max-width: ${p => p.$ancho}; margin: 0 auto;
  display: grid; grid-template-columns: repeat(${p => p.$cols}, minmax(0, 1fr)); column-gap: 28px; row-gap: 74px;
  font-family: ${FUENTE};
  .tarjeta {
    position: relative; display: flex; flex-direction: column; align-items: center; text-align: center;
    background: #fff; border-radius: 4px; box-shadow: 0 4px 18px rgba(0,0,0,.14);
    padding: 70px 16px 46px; margin-top: 60px;
  }
  .icono { position: absolute; top: -58px; left: 50%; transform: translateX(-50%); width: 116px; transition: transform .3s ease; }
  .tarjeta:hover .icono { transform: translateX(-50%) scale(1.06); }
  .icono img { width: 100%; height: auto; display: block; }
  .etiqueta { margin: 0; font-weight: 800; text-transform: uppercase; font-size: ${p => p.$tEtiqueta}; color: ${p => p.$acento}; line-height: 1.2; }
  .titulo { margin: 8px 0 0; font-weight: 400; font-size: ${p => p.$tTitulo}; line-height: 1.25; color: ${GRIS}; }
  .fecha { margin: 8px 0 0; font-size: 15px; color: ${GRIS}; }
  .progreso { width: 100%; margin-top: auto; padding-top: 26px; }
  .barra { height: 9px; border-radius: 9px; background: #e2e6ea; overflow: hidden; }
  .barra span { display: block; height: 100%; border-radius: 9px; background: ${p => p.$barra}; transition: width .6s ease; }
  .datos { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; margin-top: 8px; font-size: ${p => p.$tDatos}; color: ${GRIS}; }
  .datos strong { color: ${p => p.$barra}; text-transform: uppercase; font-weight: 800; }
  .ir {
    position: absolute; bottom: -27px; left: 50%; transform: translateX(-50%); white-space: nowrap;
    border: 0; cursor: pointer; background: ${p => p.$boton}; color: #fff;
    font-family: ${FUENTE}; font-weight: 800; font-size: ${p => p.$tBoton}; text-transform: uppercase;
    padding: 11px 30px; border-radius: 0 26px 0 26px; transition: filter .2s ease, transform .2s ease;
  }
  .ir:hover { filter: brightness(1.12); transform: translateX(-50%) translateY(-2px); }
  @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr);
    .etiqueta { font-size: 24px; } .titulo { font-size: 17px; } .ir { font-size: 16px; }
  }
`;

const fechaLarga = (s) => {
  if (!s) return '';
  try {
    return fmtFechaHoraBogota(s, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '');
  } catch (e) { return ''; }
};

export const ModulosTarjetasSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const { user } = useAuth();
  const items = Array.isArray(c.items) ? c.items : [];
  const [avance, setAvance] = useState({ modulos: {}, ultima: {}, cursos: {}, ultimaCurso: {} });
  const docIds = [...new Set(items.map(it => Number(it.documento_id)).filter(Boolean))].join(',');
  useEffect(() => {
    if (!user || !docIds) return undefined;
    let vivo = true;
    mentor2Service.progresoProyecto(docIds.split(','), { porModulo: true })
      .then(res => {
        if (!vivo || !res.data?.success) return;
        setAvance({ modulos: res.data.modulos || {}, ultima: res.data.modulos_ultima || {},
          cursos: res.data.progreso || {}, ultimaCurso: res.data.documentos_ultima || {} });
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, docIds]);

  const ir = (it) => {
    if (it.url) return irDestino(navigate, it.url);
    if (it.documento_id) {
      const destino = `/mentor/${it.documento_id}${it.modulo_id ? `?modulo=${it.modulo_id}` : ''}`;
      return navigate(destino, { state: { volverA: window.location.pathname } });
    }
    return undefined;
  };

  return (
    <SeccionBlanca $fondo={c.fondo || '#ffffff'} $padding={c.padding || '60px 0 120px'}>
      {c.titulo && <Contenido $ancho={anchoContenido(c, '1460px')}><TituloPildora c={{ ...c, titulo: tl(c.titulo) }} /></Contenido>}
      <GrillaTarjetas $cols={Number(c.columnas) || 3} $ancho={anchoVar(c.ancho_grilla, '1460px')} $acento={c.color_acento || ACENTO}
        $barra={c.color_barra || '#13897d'} $boton={c.color_boton || '#167e75'}
        $tEtiqueta={c.tamano_etiqueta || '28px'} $tTitulo={c.tamano_titulo || '18px'} $tDatos={c.tamano_datos || '12px'} $tBoton={c.tamano_boton || '17px'}>
        {items.map((it, i) => {
          // Enlazada a un módulo: su avance; enlazada a un curso entero: el del curso.
          const pct = Math.max(0, Math.min(100, Number((it.modulo_id ? avance.modulos[String(it.modulo_id)] : avance.cursos[String(it.documento_id)]) || 0)));
          const ultima = it.modulo_id ? avance.ultima[String(it.modulo_id)] : avance.ultimaCurso[String(it.documento_id)];
          return (
            <article key={it.id || i} className="tarjeta">
              {it.imagen && <span className="icono"><img src={urlImagen(it.imagen)} alt="" /></span>}
              {it.etiqueta && <h3 className="etiqueta">{tl(it.etiqueta)}</h3>}
              {it.titulo && <h2 className="titulo">{tl(it.titulo)}</h2>}
              {c.mostrar_fechas === true && it.fecha_inicio && <p className="fecha"><strong>{tl(c.texto_inicia, 'Inicia:')}</strong> {tl(it.fecha_inicio)}</p>}
              {c.mostrar_progreso !== false && (
                <div className="progreso">
                  <div className="barra"><span style={{ width: `${pct}%` }} /></div>
                  <div className="datos">
                    <strong>{pct}% {tl(c.texto_completado, 'Completado')}</strong>
                    {ultima && <span>{tl(c.texto_ultima, 'Última actividad:')} {fechaLarga(ultima)}</span>}
                  </div>
                </div>
              )}
              {(it.url || it.documento_id) && (
                <button type="button" className="ir" onClick={() => ir(it)}>{tl(it.texto_boton || c.texto_boton, 'Ir al módulo')}</button>
              )}
            </article>
          );
        })}
      </GrillaTarjetas>
    </SeccionBlanca>
  );
};
